import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "./";

const NotesModal = ({
  isOpen,
  onClose,
  onSave,
  initialNote,
  position,
  containerRef,
}) => {
  // Rect of the area the modal is allowed to occupy. Falls back to the full
  // viewport if no container ref is provided.
  const getBounds = useCallback(() => {
    if (containerRef?.current) {
      const rect = containerRef.current.getBoundingClientRect();
      return {
        top: rect.top,
        left: rect.left,
        right: rect.right,
        bottom: rect.bottom,
      };
    }
    return {
      top: 0,
      left: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
    };
  }, [containerRef]);
  const [note, setNote] = useState("");
  const modalRef = useRef(null);
  // Define initial dimensions as constants
  const initialWidth = "33.333%";
  const initialHeight = "33.333%";
  const [size, setSize] = useState({
    width: initialWidth,
    height: initialHeight,
  });
  const [resizing, setResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [startSize, setStartSize] = useState({ width: 0, height: 0 });
  const [dragging, setDragging] = useState(false);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });

  // Reset note state and size when modal opens/closes or initialNote changes
  useEffect(() => {
    if (isOpen) {
      setNote(initialNote || "");
      setSize({ width: initialWidth, height: initialHeight });

      const bounds = getBounds();
      const modalWidth = window.innerWidth * 0.33333;
      const modalHeight = window.innerHeight * 0.33333;
      const padding = 16;

      let adjustedTop = position.top;
      let adjustedLeft = position.left;

      if (adjustedTop + modalHeight > bounds.bottom) {
        adjustedTop = bounds.bottom - modalHeight - padding;
      }
      if (adjustedTop < bounds.top + padding) {
        adjustedTop = bounds.top + padding;
      }

      if (adjustedLeft + modalWidth > bounds.right) {
        adjustedLeft = bounds.right - modalWidth - padding;
      }
      if (adjustedLeft < bounds.left + padding) {
        adjustedLeft = bounds.left + padding;
      }

      setModalPosition({ top: adjustedTop, left: adjustedLeft });
    } else {
      setNote("");
    }
  }, [isOpen, initialNote, position, getBounds]);

  // Add event listeners for resize
  useEffect(() => {
    if (isOpen && resizing) {
      const handleMouseMove = (e) => {
        if (!modalRef.current || !resizing) return;

        const deltaX = e.clientX - startPos.x;
        const deltaY = e.clientY - startPos.y;

        let newWidth = startSize.width;
        let newHeight = startSize.height;

        if (resizeDirection.includes("e")) {
          newWidth = startSize.width + deltaX;
        } else if (resizeDirection.includes("w")) {
          newWidth = startSize.width - deltaX;
          if (newWidth > 200) {
            modalRef.current.style.left = `${modalPosition.left + deltaX}px`;
            setModalPosition((prev) => ({ ...prev, left: prev.left + deltaX }));
          }
        }

        if (resizeDirection.includes("s")) {
          newHeight = startSize.height + deltaY;
          // Prevent growing the modal past the container's bottom edge
          const maxHeight = getBounds().bottom - modalPosition.top - 16;
          if (newHeight > maxHeight) newHeight = maxHeight;
        } else if (resizeDirection.includes("n")) {
          newHeight = startSize.height - deltaY;
          if (newHeight > 150) {
            modalRef.current.style.top = `${modalPosition.top + deltaY}px`;
            setModalPosition((prev) => ({ ...prev, top: prev.top + deltaY }));
          }
        }

        // Enforce minimum size
        newWidth = Math.max(200, newWidth);
        newHeight = Math.max(150, newHeight);

        setSize({
          width: `${newWidth}px`,
          height: `${newHeight}px`,
        });
      };

      const handleMouseUp = () => {
        setResizing(false);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [
    isOpen,
    resizing,
    startPos,
    startSize,
    resizeDirection,
    modalPosition,
    getBounds,
  ]);

  // Add event listeners for dragging
  useEffect(() => {
    if (isOpen && dragging) {
      const handleMouseMove = (e) => {
        if (!modalRef.current || !dragging) return;

        const deltaX = e.clientX - startPos.x;
        const deltaY = e.clientY - startPos.y;

        let newLeft = modalPosition.left + deltaX;
        let newTop = modalPosition.top + deltaY;

        // Clamp drag to the container bounds (minus padding) on all sides
        const bounds = getBounds();
        const padding = 16;
        const modalWidth = modalRef.current.offsetWidth;
        const modalHeight = modalRef.current.offsetHeight;
        const maxTop = bounds.bottom - modalHeight - padding;
        const minTop = bounds.top + padding;
        const maxLeft = bounds.right - modalWidth - padding;
        const minLeft = bounds.left + padding;
        if (newTop > maxTop) newTop = maxTop;
        if (newTop < minTop) newTop = minTop;
        if (newLeft > maxLeft) newLeft = maxLeft;
        if (newLeft < minLeft) newLeft = minLeft;

        // Update modal position
        modalRef.current.style.left = `${newLeft}px`;
        modalRef.current.style.top = `${newTop}px`;

        // Update start position for next move calculation
        setStartPos({ x: e.clientX, y: e.clientY });
        setModalPosition({ top: newTop, left: newLeft });
      };

      const handleMouseUp = () => {
        setDragging(false);
        document.body.style.cursor = "default";
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isOpen, dragging, startPos, modalPosition, getBounds]);

  // Add event listener for ESC key to close modal
  useEffect(() => {
    if (isOpen) {
      const handleKeyDown = (e) => {
        if (e.key === "Escape") {
          onClose();
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [isOpen, onClose]);

  const handleResizeStart = (direction) => (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!modalRef.current) return;

    const modalRect = modalRef.current.getBoundingClientRect();
    setResizing(true);
    setResizeDirection(direction);
    setStartPos({ x: e.clientX, y: e.clientY });
    setStartSize({ width: modalRect.width, height: modalRect.height });
  };

  const handleDragStart = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!modalRef.current) return;

    setDragging(true);
    setStartPos({ x: e.clientX, y: e.clientY });
    document.body.style.cursor = "grabbing";
  };

  if (!isOpen) return null;

  return (
    <div className="h-full w-full fixed top-0 left-0 z-40" onClick={onClose}>
      <div
        ref={modalRef}
        className="flex flex-col justify-between absolute bg-lz-paper-deep dark:bg-lz-charcoal text-lz-ink-mid dark:text-lz-concrete border-2 border-lz-paper-deep-border dark:border-lz-charcoal-border rounded-2xl p-4 shadow-lg"
        style={{
          top: modalPosition.top,
          left: modalPosition.left,
          width: size.width,
          height: size.height,
          position: "absolute",
          cursor: resizing ? "grabbing" : "default",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Draggable header area */}
        <div
          className="absolute top-0 left-0 right-0 h-8 bg-lz-paper-deep dark:bg-lz-charcoal rounded-t-xl cursor-grab"
          onMouseDown={handleDragStart}
        >
          <div className="flex justify-center items-center h-full">
            <div className="w-16 h-1 bg-white rounded-full opacity-50"></div>
          </div>
        </div>

        <p className="mb-4 mt-6">
          This note will be saved in your Highlight Notes
        </p>
        <textarea
          className="w-full h-full p-2 border rounded-md mb-4 resize-none text-lz-ink-mid dark:text-white bg-lz-concrete dark:bg-lz-charcoal"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add your Highlight Notes here:"
          aria-label="Highlight notes"
          autoFocus
        />
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(note)}>Save</Button>
        </div>

        {/* Resize handles */}
        <div
          className="absolute top-0 left-0 right-0 h-1 cursor-n-resize"
          onMouseDown={handleResizeStart("n")}
        />
        <div
          className="absolute bottom-0 left-0 right-0 h-1 cursor-s-resize"
          onMouseDown={handleResizeStart("s")}
        />
        <div
          className="absolute top-0 bottom-0 left-0 w-1 cursor-w-resize"
          onMouseDown={handleResizeStart("w")}
        />
        <div
          className="absolute top-0 bottom-0 right-0 w-1 cursor-e-resize"
          onMouseDown={handleResizeStart("e")}
        />

        {/* Corner resize handles */}
        <div
          className="absolute top-0 left-0 w-4 h-4 cursor-nw-resize"
          onMouseDown={handleResizeStart("nw")}
        />
        <div
          className="absolute top-0 right-0 w-4 h-4 cursor-ne-resize"
          onMouseDown={handleResizeStart("ne")}
        />
        <div
          className="absolute bottom-0 left-0 w-4 h-4 cursor-sw-resize"
          onMouseDown={handleResizeStart("sw")}
        />
        <div
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
          onMouseDown={handleResizeStart("se")}
        />
      </div>
    </div>
  );
};

export default NotesModal;
